// @djsem/render-state — Pro Integrations Mapping Editor (Prompt 25R).
// Editor layer OVER the tested 12F ProIntegrationDryRunAdapterService (one adapter system):
// mapping CRUD, test cues, adapter status, and an output log — all dry-run only.

import {
  ProIntegrationDryRunAdapterService,
  type DJSEMOutputAdapterProfile, type DJSEMOutputAdapterMapping, type DJSEMOutputAdapterDryRunEvent,
} from "./proAdapters";
import { type DJSEMValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";

/** Current readiness status of an output adapter profile, as reported by {@link AdapterMappingEditorService.getAdapterStatus}. */
export interface DJSEMAdapterStatus {
  profileId: string;
  state: "disabled" | "ready_dry_run" | "invalid";
  lastTestAt?: string;
  lastEventCount: number;
  warnings: string[];
}

/** Input for {@link AdapterMappingEditorService.addMapping}. */
export interface AddMappingInput { profile: DJSEMOutputAdapterProfile; mapping: Omit<DJSEMOutputAdapterMapping, "id"> & { id?: string }; }
/** Input for {@link AdapterMappingEditorService.updateMapping}. */
export interface UpdateMappingInput { profile: DJSEMOutputAdapterProfile; mappingId: string; patch: Partial<Omit<DJSEMOutputAdapterMapping, "id">>; }
/** Input for {@link AdapterMappingEditorService.removeMapping}. */
export interface RemoveMappingInput { profile: DJSEMOutputAdapterProfile; mappingId: string; }
/** Input for {@link AdapterMappingEditorService.sendTestCue}. */
export interface SendTestCueInput { profile: DJSEMOutputAdapterProfile; cue: { cueId: string; intensity?: number }; createdAt?: string; }

const _seq = new SeqCounter();

/**
 * Editor layer over the tested dry-run pro-integration adapter service: mapping CRUD, test
 * cues, adapter status, and an output log — all dry-run only.
 */
export class AdapterMappingEditorService {
  private adapters = new ProIntegrationDryRunAdapterService();
  private log: DJSEMOutputAdapterDryRunEvent[] = [];

  /**
   * Appends a new mapping to an adapter profile and re-validates it.
   * @param input - the profile and the mapping to add.
   * @returns the updated profile and its validation result.
   */
  addMapping(input: AddMappingInput): { profile: DJSEMOutputAdapterProfile; validation: DJSEMValidationResult } {
    const mapping: DJSEMOutputAdapterMapping = { ...input.mapping, id: input.mapping.id ?? `map_${_seq.next()}` };
    const profile = { ...input.profile, mappings: [...input.profile.mappings, mapping] };
    return { profile, validation: this.adapters.validateProfile(profile) };
  }

  /**
   * Patches an existing mapping on an adapter profile and re-validates it.
   * @param input - the profile, the mapping id to patch, and the patch itself.
   * @returns the updated profile and its validation result.
   */
  updateMapping(input: UpdateMappingInput): { profile: DJSEMOutputAdapterProfile; validation: DJSEMValidationResult } {
    const profile = {
      ...input.profile,
      mappings: input.profile.mappings.map((m) => (m.id === input.mappingId ? { ...m, ...input.patch, id: m.id } : m)),
    };
    return { profile, validation: this.adapters.validateProfile(profile) };
  }

  /**
   * Removes a mapping from an adapter profile.
   * @param input - the profile and the mapping id to remove.
   * @returns the updated profile.
   */
  removeMapping(input: RemoveMappingInput): DJSEMOutputAdapterProfile {
    return { ...input.profile, mappings: input.profile.mappings.filter((m) => m.id !== input.mappingId) };
  }

  /**
   * Maps and logs a test cue as a themed event — dry-run only.
   * @param input - the profile and the test cue (id + optional intensity).
   * @returns the dry-run event produced (also appended to the output log).
   */
  sendTestCue(input: SendTestCueInput): DJSEMOutputAdapterDryRunEvent {
    const ev = this.adapters.mapThemedEvent({
      profile: input.profile,
      themedEvent: { test: true, cueId: input.cue.cueId, intensity: input.cue.intensity ?? 0.5 },
      createdAt: input.createdAt,
    });
    this.log.push(ev);
    return ev;
  }

  /** @returns a copy of every dry-run event logged so far. */
  getOutputLog(): DJSEMOutputAdapterDryRunEvent[] { return [...this.log]; }
  /** Clears the dry-run output log. */
  clearOutputLog(): void { this.log = []; }

  /**
   * @param profile - the adapter profile to check.
   * @param lastTestAt - optional timestamp of the last test cue sent.
   * @returns the profile's current readiness status, event count, and warnings.
   */
  getAdapterStatus(profile: DJSEMOutputAdapterProfile, lastTestAt?: string): DJSEMAdapterStatus {
    const v = this.adapters.validateProfile(profile);
    const warnings = v.ok ? [...profile.warnings] : v.issues.map((i) => i.message);
    return {
      profileId: profile.id,
      state: !v.ok ? "invalid" : profile.enabled ? "ready_dry_run" : "disabled",
      lastTestAt,
      lastEventCount: this.log.filter((e) => e.adapterProfileId === profile.id).length,
      warnings,
    };
  }
}
